import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { devLog, devError } from '../../utils/devLogger';
import styled from 'styled-components';
import { isShoppingMode } from '../../config/appMode';
import { canCancelBid, getBidCancelDisabledReason } from '../../utils/bidCancelUtils';
import { parseApiDateTime } from '../../utils/dateUtils';
import LotCard from '../../customComponents/FavoriteCard';
import FilterChips, { ChipItem } from '../../components/common/FilterChips';
import NoDataContainer from '../../components/NoDataContainer';
import DataLoader from '../../components/DataLoader';
import Modal from '../../components/Modal';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import { navigateWithScrollTop } from '../../utils/navigationScroll';
import { LikedLotData, BidHistoryData } from '../../lib/api/user/userApi.types';
import { likeLot, getBidHistory, cancelPreBid } from '../../lib/api/user/userApi';
import { AppColors } from '../../styles/colors';

const BidHistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0; /* flex item이 제대로 축소될 수 있도록 */
`;

// 디바이더
const ChipsDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #f3f3f3;
  margin-top: 16px;
`;

// 모달 버튼 스타일
const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const CancelModalButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background-color: ${AppColors.btnC};
  color: ${AppColors.onBtnC};
  
  &:hover {
    background-color: #e0e0e0;
  }
`;

const ConfirmModalButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background-color: ${AppColors.btnCEmphasis};
  color: ${AppColors.onBtnCEmphasis};
  
  &:hover {
    background-color: #262626;
    color: #FFFFFF;
  }
`;

// 입찰 내역 카드 컨테이너 (그리드)
const BidHistoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding: 24px 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
    padding: 32px 0;
  }
`;



// 다국어 텍스트
const bidHistoryTexts = {
  ko: {
    myBidAmount: isShoppingMode() ? '나의 구매가' : '나의 입찰가',
    currentHighestBid: '현재 최고가',
    bidDate: isShoppingMode() ? '구매일시' : '입찰일시',
    noData: isShoppingMode() ? '구매 내역이 없습니다' : '입찰 내역이 없습니다',
    bidActive: isShoppingMode() ? '구매중' : '입찰중',
    bidWon: isShoppingMode() ? '구매완료' : '낙찰',
    bidLost: '유찰',
    auctionEnded: isShoppingMode() ? '판매종료' : '경매종료',
    // FavoriteCard용 텍스트들
    preSubmission: isShoppingMode() ? '구매하기' : '사전입찰하기',
    liveView: '라이브참여하기',
    liveBid: isShoppingMode() ? '구매하기' : '경매등록하기',
    liveStartPrice: isShoppingMode() ? '판매가' : '라이브 시작가',
    startPrice: isShoppingMode() ? '판매가' : '시작가',
    winningPrice: isShoppingMode() ? '결제금액' : '낙찰가',
    // BidHistory 전용 텍스트들
    cancelBid: isShoppingMode() ? '구매취소' : '입찰취소',
    cancelBidBlockedTitle: isShoppingMode() ? '구매취소 불가' : '입찰취소 불가',
    confirm: '확인',
    // 모달 관련 텍스트들
    cancelBidModalTitle: isShoppingMode() ? '구매를 취소하시겠습니까?' : '입찰을 취소하시겠습니까?',
    cancelBidModalMessage: isShoppingMode() ? '취소 후에는 해당 구매 내역이 무효 처리되며, 다시 복구할 수 없습니다.' : '취소 후에는 해당 입찰 내역이 무효 처리되며, 다시 복구할 수 없습니다.',
    cancel: '취소',
    confirmCancel: isShoppingMode() ? '구매취소' : '입찰취소',
    // 최고/차순위 사전 입찰자 배지 텍스트
    topPreBidder: 'Winning',
    secondPreBidder: 'Loosing',
    // 필터 칩 텍스트들
    filters: {
      all: '전체',
      preSubmission: isShoppingMode() ? '판매중' : '사전입찰중',
      live: isShoppingMode() ? '판매중' : '라이브중',
      ended: isShoppingMode() ? '판매종료' : '경매종료'
    }
  },
  en: {
    myBidAmount: isShoppingMode() ? 'My Purchase Price' : 'My Bid',
    currentHighestBid: 'Current Highest',
    bidDate: isShoppingMode() ? 'Purchase Date' : 'Bid Date',
    noData: isShoppingMode() ? 'No purchase history' : 'No bid history',
    bidActive: isShoppingMode() ? 'PURCHASING' : 'ACTIVE',
    bidWon: isShoppingMode() ? 'PURCHASED' : 'WON',
    bidLost: 'LOST',
    auctionEnded: isShoppingMode() ? 'Sale Ended' : 'Auction Ended',
    // FavoriteCard용 텍스트들
    preSubmission: isShoppingMode() ? 'Purchase' : 'Pre-Bid',
    liveView: 'Live View',
    liveBid: isShoppingMode() ? 'Purchase' : 'Live Bid',
    liveStartPrice: isShoppingMode() ? 'Price' : 'Live Starting Price',
    startPrice: isShoppingMode() ? 'Price' : 'Starting Price',
    winningPrice: isShoppingMode() ? 'Payment Amount' : 'Winning Bid',
    // BidHistory 전용 텍스트들
    cancelBid: isShoppingMode() ? 'Cancel Purchase' : 'Cancel Bid',
    cancelBidBlockedTitle: isShoppingMode() ? 'Cancellation Unavailable' : 'Cancellation Unavailable',
    confirm: 'OK',
    // 모달 관련 텍스트들
    cancelBidModalTitle: isShoppingMode() ? 'Do you want to cancel your purchase?' : 'Do you want to cancel your bid?',
    cancelBidModalMessage: isShoppingMode() ? 'After cancellation, your purchase will be invalidated and cannot be recovered.' : 'After cancellation, your bid will be invalidated and cannot be recovered.',
    cancel: 'Cancel',
    confirmCancel: isShoppingMode() ? 'Cancel Purchase' : 'Cancel Bid',
    // 최고/차순위 사전 입찰자 배지 텍스트
    topPreBidder: 'Winning',
    secondPreBidder: 'Loosing',
    // 필터 칩 텍스트들
    filters: {
      all: 'All',
      preSubmission: isShoppingMode() ? 'On Sale' : 'Pre-Bidding',
      live: isShoppingMode() ? 'On Sale' : 'Live',
      ended: isShoppingMode() ? 'Sale Ended' : 'Ended'
    }
  }
};

// 요일 다국어 텍스트
const dayNamesLocalized = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
};

// 시간 포맷 유틸리티 함수
const formatLiveTime = (liveTime: string, language: 'ko' | 'en' = 'ko'): string => {
  try {
    const targetDate = new Date(liveTime);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
    if (now >= targetDate) {
      return 'LIVE';
    }
    
    if (targetDay.getTime() === today.getTime()) {
      const diffMs = targetDate.getTime() - now.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes}m`;
      } else {
        return `${diffMinutes}m`;
      }
    }
    
    const month = targetDate.getMonth() + 1;
    const date = targetDate.getDate();
    const dayNames = dayNamesLocalized[language];
    const dayName = dayNames[targetDate.getDay()];
    const hours = targetDate.getHours().toString().padStart(2, '0');
    const minutes = targetDate.getMinutes().toString().padStart(2, '0');
    
    return `${month}/${date}(${dayName}) ${hours}:${minutes}`;
  } catch (error) {
    return liveTime;
  }
};


// LiveEndTime 포맷팅 함수 (07/17(금) 13:00 종료 / Jul 17(Fri) 13:00 Ends)
const formatLiveEndTime = (dateStr: string, language: 'ko' | 'en' = 'ko'): string => {
  try {
    const date = new Date(dateStr);
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // mm
    const day = date.getDate(); // d (패딩 없음)
    const hours = date.getHours().toString().padStart(2, '0'); // hh
    const minutes = date.getMinutes().toString().padStart(2, '0'); // MM
    const dayName = dayNamesLocalized[language][date.getDay()]; // 요일
    
    if (language === 'ko') {
      return `${month}/${day}(${dayName}) ${hours}:${minutes} 종료`;
    } else {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[date.getMonth()];
      return `${monthName} ${day}(${dayName}) ${hours}:${minutes} Ends`;
    }
  } catch (error) {
    return dateStr;
  }
};

// BidHistoryData를 LikedLotData 형식으로 변환하는 함수
const convertBidHistoryToLikedLot = (bidData: BidHistoryData): LikedLotData => {
  // 필수 필드 체크
  if (!bidData.lot || !bidData.auction) {
    devError('Missing lot or auction data:', bidData);
    throw new Error('Missing required data');
  }
  
  const convertedData = {
    index: bidData.lot.index,
    agreed: bidData.agreed ?? bidData.lot.agreed,
    code: bidData.lot.code,
    name: bidData.lot.name,
    description: bidData.lot.description,
    images: bidData.lot.images || [],
    startPrice: bidData.lot.startPrice,
    exchangeRate: bidData.lot.exchangeRate || bidData.exchangeRate,
    startPriceUsd: bidData.lot.startPriceUsd || (bidData.lot.startPrice / (bidData.exchangeRate || 1)),
    recommendNo: bidData.lot.recommendNo || 0,
    likedUserIndexes: bidData.lot.likedUserIndexes || [],
    likeCount: bidData.lot.likeCount || 0,
    createBy: bidData.lot.createBy || '',
    updateBy: bidData.lot.updateBy || '',
    createAt: bidData.lot.createAt,
    updateAt: bidData.lot.updateAt || "",
    category: bidData.lot.category || { code: '', name: '', krName: '' },
    subCategory: bidData.lot.subCategory || { code: '', name: '', krName: '' },
    auction: {
      index: bidData.auction.index,
      name: bidData.auction.name,
      description: bidData.auction.description,
      url: bidData.auction.url,
      auctionLotNo: 1, // API 응답에 없으므로 기본값
      preBidStartAt: bidData.auction.preBidStartAt,
      preBidEndAt: bidData.auction.preBidEndAt,
      auctionStartAt: bidData.auction.auctionStartAt,
      auctionEndAt: bidData.auction.auctionEndAt,
      paymentDueAt: bidData.auction.paymentDueAt,
      auctionFeeRate: bidData.auction.auctionFeeRate,
      platformFeeRate: bidData.auction.platformFeeRate,
      createAt: bidData.auction.createAt,
      updateAt: bidData.auction.updateAt || ''
    },
    auctioneer: bidData.auction.auctioneer ? {
      index: bidData.auction.auctioneer.index,
      name: bidData.auction.auctioneer.name,
      description: bidData.auction.auctioneer.description || '',
      profilePic: bidData.auction.auctioneer.profile_pic || ''
    } : {
      index: 0,
      name: 'Unknown Auctioneer',
      description: '',
      profilePic: ''
    }
  };
  
  // lot.ended 필드와 highestBid를 추가로 전달
  (convertedData as any).apiData = {
    ended: bidData.lot.ended,
    highestBid: (bidData as any).highestBid || (bidData.lot as any).highestBid || 0,
    bids: Array.isArray((bidData as any).bids) ? (bidData as any).bids : [],
    isWinning: bidData.isWinning
  };
  
  return convertedData;
};

const BidHistory: React.FC = () => {
  const { lang } = useLang();
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const texts = bidHistoryTexts[lang];
  
  // 가격 포맷팅 함수 - 사용자 locationType에 따라 KRW 또는 USD 표시
  const formatPrice = (price: number, language: 'ko' | 'en', exchangeRate: number): string => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    if (isDomestic) {
      return `KRW ${price.toLocaleString()}`;
    } else {
      // USD로 표시 (환율 정보 활용) - 소수점 2자리까지 표시
      const usdPrice = price / exchangeRate;
      return `USD ${usdPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };
  
  // 상태 관리
  const [bidHistory, setBidHistory] = useState<LikedLotData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // 필터 상태 관리
  const [activeFilter, setActiveFilter] = useState<string>('all');
  
  // 입찰 취소 모달 상태 관리
  const [cancelBidModalState, setCancelBidModalState] = useState<{
    isOpen: boolean;
    selectedItem: LikedLotData | null;
    mode: 'confirm' | 'blocked';
    blockedReason: string;
  }>({
    isOpen: false,
    selectedItem: null,
    mode: 'confirm',
    blockedReason: ''
  });
  
  // Intersection Observer를 위한 ref
  const observerTarget = useRef<HTMLDivElement>(null);

  // 필터 칩 데이터 생성
  const getFilterChips = (): ChipItem[] => {
    return [
      { key: 'all', label: texts.filters.all },
      { key: 'preSubmission', label: texts.filters.preSubmission },
      { key: 'live', label: texts.filters.live },
      { key: 'ended', label: texts.filters.ended }
    ];
  };

  // 필터 칩 클릭 핸들러
  const handleFilterChipClick = (filterKey: string) => {
    setActiveFilter(filterKey);
    setCurrentPage(1);
    setHasMore(true);
    devLog('Filter changed to:', filterKey);
    // 필터 변경 시 데이터 재로드 (명시적으로 filterKey 전달)
    fetchBidHistory(1, true, filterKey);
  };

  // 입찰 내역 API 호출 함수 (필터 변경 및 페이징용)
  const fetchBidHistory = useCallback(async (page: number, isInitial: boolean = false, filterStatus?: string) => {
    if (!user || !user.index) {
      return;
    }

    try {
      if (isInitial) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      // 필터에 따른 쿼리 파라미터 설정
      const getFilterParams = (filter: string) => {
        switch (filter) {
          case 'all':
            return { status: 'all' as const };
          case 'preSubmission':
            return { status: 'preBid' as const };
          case 'live':
            return { status: 'live' as const };
          case 'ended':
            return { status: 'ended' as const };
          default:
            return { status: 'all' as const };
        }
      };

      // filterStatus가 제공되면 그것을 사용하고, 없으면 현재 activeFilter 사용
      const currentFilter = filterStatus || activeFilter;
      
      const response = await getBidHistory({
        page,
        limit: 30,
        ...getFilterParams(currentFilter)
      });
      
      if (response.data.statusCode === 200) {
        // data가 존재하고 배열인지 확인
        if (response.data.data && Array.isArray(response.data.data)) {
          // BidHistoryData[]를 LikedLotData[]로 변환
          const convertedData = response.data.data.map((item, index) => {
            try {
              const converted = convertBidHistoryToLikedLot(item);
              // 고유 식별자를 위한 bidInfo 추가
              (converted as any).bidInfo = {
                amount: item.amount,
                createdAt: item.createdAt,
                originalIndex: index
              };
              return converted;
            } catch (error) {
              devError(`Error converting bid history item:`, error, item);
              return null;
            }
          }).filter(item => item !== null); // null 항목 제거
          
          if (isInitial) {
            setBidHistory(convertedData);
          } else {
            // 기존 데이터에 새 데이터 누적
            setBidHistory(prev => [...prev, ...convertedData]);
          }
          
          // 받은 데이터가 30개 미만이면 더 이상 데이터가 없음
          setHasMore(convertedData.length === 30);
          setCurrentPage(page);
        } else {
          // data가 null이거나 배열이 아닌 경우
          if (isInitial) {
            setBidHistory([]);
          }
          setHasMore(false);
        }
      } else {
        if (isInitial) {
          setBidHistory([]);
          setError(response.data.message || '입찰 내역을 불러올 수 없습니다.');
        }
        setHasMore(false);
      }
    } catch (error) {
      devError('Failed to fetch bid history:', error);
      if (isInitial) {
        setBidHistory([]);
        setError('입찰 내역을 불러오는 중 오류가 발생했습니다.');
      }
      setHasMore(false);
    } finally {
      if (isInitial) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [user, activeFilter]);

  // 초기 데이터 로드
  useEffect(() => {
    if (user && user.index) {
      // 초기 로드는 항상 'all' 상태로 고정
      const loadInitialData = async () => {
        try {
          setLoading(true);
          setError(null);
          
          const response = await getBidHistory({
            page: 1,
            limit: 30,
            status: 'all'
          });
          
          if (response.data.statusCode === 200) {
            if (response.data.data && Array.isArray(response.data.data)) {
              const convertedData = response.data.data.map((item, index) => {
                try {
                  const converted = convertBidHistoryToLikedLot(item);
                  (converted as any).bidInfo = {
                    amount: item.amount,
                    createdAt: item.createdAt,
                    originalIndex: index
                  };
                  return converted;
                } catch (error) {
                  devError(`Error converting bid history item:`, error, item);
                  return null;
                }
              }).filter(item => item !== null);
              
              setBidHistory(convertedData);
              setHasMore(convertedData.length === 30);
              setCurrentPage(1);
            } else {
              setBidHistory([]);
              setHasMore(false);
            }
          } else {
            setBidHistory([]);
            setError(response.data.message || '입찰 내역을 불러올 수 없습니다.');
            setHasMore(false);
          }
        } catch (error) {
          devError('Failed to fetch initial bid history:', error);
          setBidHistory([]);
          setError('입찰 내역을 불러오는 중 오류가 발생했습니다.');
          setHasMore(false);
        } finally {
          setLoading(false);
        }
      };
      
      loadInitialData();
    }
  }, [user]);

  // Intersection Observer를 통한 무한 스크롤 구현
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
          fetchBidHistory(currentPage + 1, false);
        }
      },
      {
        threshold: 0.1, // 10% 보이면 다음 페이지 로드
        rootMargin: '100px' // 100px 전에 미리 로드
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadingMore, currentPage, fetchBidHistory]);

  // 좋아요 토글 핸들러
  const handleFavoriteToggle = async (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    
    if (!user || !user.index) return;

    try {
      await likeLot(index);
      // 성공 시 목록에서 해당 아이템의 좋아요 상태 업데이트
      setBidHistory(prevItems => 
        prevItems.map(item => {
          if (item.index === index) {
            const userIndex = user.index!; // 이미 위에서 체크했으므로 ! 사용
            const isCurrentlyLiked = item.likedUserIndexes.includes(userIndex);
            return {
              ...item,
              likedUserIndexes: isCurrentlyLiked
                ? item.likedUserIndexes.filter(uIndex => uIndex !== userIndex)
                : [...item.likedUserIndexes, userIndex],
              likeCount: isCurrentlyLiked ? item.likeCount - 1 : item.likeCount + 1
            };
          }
          return item;
        })
      );
    } catch (error) {
      devError('좋아요 처리 실패:', error);
      alert('좋아요 처리에 실패했습니다.');
    }
  };

  const handleCardClick = (item: LikedLotData) => {
    devLog('입찰 내역 클릭:', item);
    
    const auctionStatus = getAuctionStatus(item);
    
    // 경매가 종료된 경우 클릭 무시
    if (auctionStatus === 'ended') {
      return;
    }
    
    // 상태에 따라 다른 경로로 이동
    switch (auctionStatus) {
      case 'before-submission':
      case 'submission-period':
      case 'before-live':
        // 사전입찰 상태: pre-bid/{lotIndex}
        navigateWithScrollTop(navigate, `/pre-bid/${item.index}`);
        break;
      case 'live':
        // 라이브 중: 라이브참여하기 버튼과 동일하게 /live-bid로 이동하고 transformedItem으로 데이터 변환
        const transformedItem = {
          ...item,
          apiData: {
            index: item.index,
            auction: item.auction
          },
          auctionIndex: item.auction?.index,
          lotIndex: item.index,
          id: item.index,
          lotId: item.index
        };
        navigateWithScrollTop(navigate, '/live-bid', { state: { selectedItem: transformedItem, auctionIndex: item.auction?.index, lotIndex: item.index } });
        break;
      default:
        break;
    }
  };

  // 입찰 취소 핸들러
  const handleCancelBid = (item: LikedLotData) => {
    devLog('입찰 취소 요청:', item);
    
    // 입찰 취소 가능 여부 확인
    const auctionStatus = getAuctionStatus(item);

    const canCancel = !!item.auction?.auctionStartAt && canCancelBid(item.auction.auctionStartAt, auctionStatus);

    if (!canCancel) {
      const blockedReason = getBidCancelDisabledReason(item.auction?.auctionStartAt || '', auctionStatus, lang);
      setCancelBidModalState({
        isOpen: true,
        selectedItem: null,
        mode: 'blocked',
        blockedReason
      });
      return;
    }

    setCancelBidModalState({
      isOpen: true,
      selectedItem: item,
      mode: 'confirm',
      blockedReason: ''
    });
  };

  // 입찰 취소 확인 핸들러
  const handleConfirmCancelBid = async () => {
    if (!cancelBidModalState.selectedItem) return;
    
    try {
      devLog('입찰 취소 API 호출 시작:', cancelBidModalState.selectedItem);
      
      // LOT index만 확인 (정확한 값만 사용)
      const selectedItem = cancelBidModalState.selectedItem;
      const lotId = selectedItem.index;
      
      devLog('추출된 lotId:', lotId);
      
      if (!lotId) {
        devError('LOT index를 찾을 수 없습니다:', selectedItem);
        return;
      }
      
      // 추가 방어 로직: API 호출 직전에 다시 한 번 확인
      const auctionStatus = getAuctionStatus(selectedItem);
      
      if (!selectedItem.auction?.auctionStartAt || !canCancelBid(selectedItem.auction.auctionStartAt, auctionStatus)) {
        const disabledReason = getBidCancelDisabledReason(selectedItem.auction?.auctionStartAt || '', auctionStatus, lang);

        // 확인 직전에 조건이 바뀐 경우(시간 경과 등) 사유 모달로 전환
        setCancelBidModalState({
          isOpen: true,
          selectedItem: null,
          mode: 'blocked',
          blockedReason: disabledReason
        });
        return;
      }
      
      // 실제 API 호출로 입찰 취소 처리
      const response = await cancelPreBid(lotId);
      
      devLog('입찰 취소 API 응답:', response);
      
      if (response.data.statusCode === 200) {
        devLog('입찰 취소 성공');
        
        // 모달 닫기
        setCancelBidModalState({
          isOpen: false,
          selectedItem: null,
          mode: 'confirm',
          blockedReason: ''
        });
        
        // 데이터 새로고침 (현재 페이지와 필터 유지)
        fetchBidHistory(1, true);
      } else {
        devError('입찰 취소 실패:', response.data);
        // 모달 닫기
        setCancelBidModalState({
          isOpen: false,
          selectedItem: null,
          mode: 'confirm',
          blockedReason: ''
        });
      }
    } catch (error) {
      devError('입찰 취소 API 호출 중 오류:', error);
      // 모달 닫기
      setCancelBidModalState({
        isOpen: false,
        selectedItem: null,
        mode: 'confirm',
        blockedReason: ''
      });
    }
  };

  // 입찰 취소 모달 닫기
  const handleCancelBidModalClose = () => {
    setCancelBidModalState({
      isOpen: false,
      selectedItem: null,
      mode: 'confirm',
      blockedReason: ''
    });
  };

  // 경매 상태 확인 함수
  const getAuctionStatus = (item: LikedLotData): 'before-submission' | 'submission-period' | 'before-live' | 'live' | 'ended' => {
    // lot.ended가 true이면 강제로 ended 상태 반환
    if ((item as any).ended === true) {
      return 'ended';
    }
    
    const now = new Date();
    
    if (!item.auction) return 'ended';
    
    const preBidStartAt = parseApiDateTime(item.auction.preBidStartAt);
    const preBidEndAt = parseApiDateTime(item.auction.preBidEndAt);
    const auctionStartAt = parseApiDateTime(item.auction.auctionStartAt);
    const auctionEndAt = parseApiDateTime(item.auction.auctionEndAt);

    if (!preBidStartAt || !preBidEndAt || !auctionStartAt || !auctionEndAt) return 'ended';
    
    if (now > auctionEndAt) return 'ended';
    if (now < preBidStartAt) return 'before-submission';
    if (now >= preBidStartAt && now < preBidEndAt) return 'submission-period';
    if (now >= preBidEndAt && now < auctionStartAt) return 'before-live';
    return 'live';
  };

  const renderBidHistoryCard = (item: LikedLotData, index: number) => {
    const auctionStatus = getAuctionStatus(item);
    
    // bidInfo를 활용한 고유한 key 생성
    const bidInfo = (item as any).bidInfo;
    const uniqueKey = bidInfo 
      ? `bid-${item.index}-${bidInfo.amount}-${bidInfo.createdAt}-${bidInfo.originalIndex}`
      : `bid-${index}-${item.index}-${Date.now()}`;

    // BidHistory는 내 bid만 오므로 응답의 isWinning 값으로 배지 표기
    const isWinning = (item as any).apiData?.isWinning as boolean | null | undefined;
    const isTopPreBidder = !isShoppingMode() && isWinning === true;
    const isSecondPreBidder = !isShoppingMode() && isWinning === false;
    
    return (
      <LotCard
        key={uniqueKey}
        item={item}
        auctionStatus={auctionStatus}
        texts={{
          preSubmission: texts.preSubmission,
          liveView: texts.liveView,
          liveBid: texts.liveBid,
          auctionEnded: texts.auctionEnded,
          liveStartPrice: texts.liveStartPrice,
          startPrice: texts.startPrice,
          winningPrice: texts.winningPrice,
          cancelBid: texts.cancelBid
        }}
        exchangeRate={item.exchangeRate}
        formatLiveTime={formatLiveTime}
        formatPrice={formatPrice}
        formatLiveEndTime={formatLiveEndTime}
        onCardClick={handleCardClick}
        onFavoriteToggle={handleFavoriteToggle}
        isBidHistory={true}
        onCancelBid={handleCancelBid}
        showFavoriteIcon={true}
        // LOT 상세 카드와 동일하게 나의 최고/차순위 입찰 배지 표시
        isTopPreBidder={isTopPreBidder}
        topPreBidderLabel={texts.topPreBidder}
        isSecondPreBidder={isSecondPreBidder}
        secondPreBidderLabel={texts.secondPreBidder}
      />
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 200px)', /* 앱바와 필터 영역을 제외한 높이 */
          width: '100%'
        }}>
          <DataLoader/>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          minHeight: 'calc(100vh - 200px)'
        }}>
          <NoDataContainer message={texts.noData} imageSize={107} />
        </div>
      );
    }

    if (bidHistory.length === 0) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          minHeight: 'calc(100vh - 200px)'
        }}>
          <NoDataContainer message={texts.noData} imageSize={107} />
        </div>
      );
    }

    return (
      <>
        <BidHistoryGrid>
          {bidHistory.map(renderBidHistoryCard)}
        </BidHistoryGrid>
        
        {/* Intersection Observer 타겟 */}
        <div ref={observerTarget} style={{ height: '20px' }} />
        
        {/* 추가 로딩 상태 */}
        {loadingMore && (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: '3px solid #e0e0e0',
                borderTop: '3px solid #666',
                animation: 'spin 1s linear infinite',
              }}
            />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
        
        {/* 더 이상 데이터가 없을 때 */}
        {!hasMore && bidHistory.length > 0 && (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: '#999',
            fontSize: '14px'
          }}>
            {/* 모든 입찰 내역을 불러왔습니다. */}
          </div>
        )}
      </>
    );
  };

  return (
    <BidHistoryContainer>
      {/* 필터 칩스 - 쇼핑모드에서는 숨김 */}
      {!isShoppingMode() && (
        <>
          <FilterChips
            chips={getFilterChips()}
            selectedChips={[activeFilter]}
            onChipClick={handleFilterChipClick}
            allowMultiSelect={false}
          />
          
          {/* 디바이더 */}
          <ChipsDivider />
        </>
      )}
      
      <ContentContainer>
        {renderContent()}
      </ContentContainer>
      
      {/* 입찰 취소 모달 */}
      <Modal
        isOpen={cancelBidModalState.isOpen}
        onClose={handleCancelBidModalClose}
        header={cancelBidModalState.mode === 'confirm' ? texts.cancelBidModalTitle : texts.cancelBidBlockedTitle}
        bgColor="white"
        body={cancelBidModalState.mode === 'confirm' ? texts.cancelBidModalMessage : (cancelBidModalState.blockedReason || '')}
        footer={
          cancelBidModalState.mode === 'confirm' ? (
            <ButtonContainer>
              <CancelModalButton onClick={handleCancelBidModalClose}>
                {texts.cancel}
              </CancelModalButton>
              <ConfirmModalButton onClick={handleConfirmCancelBid}>
                {texts.confirmCancel}
              </ConfirmModalButton>
            </ButtonContainer>
          ) : (
            <ButtonContainer>
              <ConfirmModalButton onClick={handleCancelBidModalClose}>
                {texts.confirm}
              </ConfirmModalButton>
            </ButtonContainer>
          )
        }
      />
    </BidHistoryContainer>
  );
};

export default BidHistory;
